import React, { forwardRef, useEffect, useState, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import ReadinessGauge from '@/components/aegis/ReadinessGauge';
import { TrustAnchors } from '@/components/aegis/profile/TrustAnchors';
import { CircleOfAction } from '@/components/aegis/profile/CircleOfAction';
import { getAccountHealth } from '@/lib/accountIntegrity';
import { useSession } from '@/hooks/useSession';

interface Props {
  onClose?: () => void;
  placement?: 'top' | 'bottom';
}

type PanelHandle = { clearIdle: () => void; startIdle: () => void; };

const ProfilePanel = React.forwardRef<PanelHandle, Props>(function ProfilePanel({ onClose, placement = 'bottom' }: Props, ref) {
  const { user } = useSession();
  type Health = { score?: number } | null;
  const [health, setHealth] = useState<Health>(null);
  const idleTimerRef = React.useRef<number | null>(null);

  const clearIdle = React.useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const startIdle = React.useCallback(() => {
    clearIdle();
    idleTimerRef.current = window.setTimeout(() => {
      onClose?.();
    }, 5000);
  }, [clearIdle, onClose]);

  // expose controls to parent
  React.useImperativeHandle(ref, () => ({ clearIdle, startIdle }), [clearIdle, startIdle]);

  useEffect(() => {
    // Start idle timer on mount
    startIdle();
    return () => clearIdle();
  }, [startIdle, clearIdle]);

  useEffect(() => {
    async function load() {
      if (!user) return;
      const h = await getAccountHealth(user.id);
      setHealth(h);
    }
    load();
  }, [user]);

  const readiness = (() => {
    if (!health) return { label: 'Unknown', ring: 'ring-border' };
    if (health.score >= 80) return { label: 'High', ring: 'ring-green-400' };
    if (health.score >= 50) return { label: 'Medium', ring: 'ring-yellow-400' };
    return { label: 'Low', ring: 'ring-blue-300' };
  })();

  return (
    <div className="w-[480px] rounded-2xl bg-card p-0 shadow-lg border border-border flex flex-col max-h-[75vh]">
      {/* Top row */}
      <div
        className="p-4 flex items-center gap-4"
        onMouseEnter={clearIdle}
        onMouseLeave={startIdle}
        onFocus={clearIdle}
        onBlur={startIdle}
        tabIndex={-1}
      >
        <div className="relative">
          <div className={`h-16 w-16 rounded-full p-1 bg-card ${readiness.ring} animate-pulse ring-2 ring-offset-1`}>
            {user?.user_metadata?.avatar_url ? (
              <img src={user.user_metadata.avatar_url} alt="avatar" className="h-full w-full rounded-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-semibold text-foreground">{(user?.user_metadata?.full_name || user?.email || 'You').slice(0,2)}</div>
            )}
          </div>
          <span className="absolute -bottom-1 -right-1 inline-flex items-center rounded-full bg-card p-0.5 ring-1 ring-border">
            <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{user?.user_metadata?.full_name || user?.email}</h3>
            <span className="text-sm text-muted-foreground">({user?.user_metadata?.preferred_name || '—'})</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">This card shows your identity and readiness. Institutions and family see this only when needed.</p>
        </div>

        <div className="w-32 text-right">
          <div className="text-xs text-muted-foreground">Continuity Readiness</div>
          <div className="mt-2 flex items-center justify-end gap-2">
            <div className="flex items-center gap-2">
              <div className="mr-2">
                <ReadinessGauge />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content (scrollable) */}
      <div className="overflow-auto px-4 pb-4 pt-2 flex-1">
        <div className="grid grid-cols-2 gap-3">        <div className="rounded-lg border border-border p-2">
          <h4 className="text-sm font-medium">Trust Anchors</h4>
          <div className="mt-2 flex items-center justify-center">
            <TrustAnchors />
          </div>
        </div>

        <div className="rounded-lg border border-border p-2">
          <h4 className="text-sm font-medium">Status Pulse</h4>
          <div className="mt-2 flex items-center justify-center">
            <div className="relative h-20 w-20 flex items-center justify-center">
              <span className="absolute inline-flex h-10 w-10 rounded-full bg-primary/20 animate-pulse blur-sm opacity-80" />
              <div className="relative z-10 flex items-center justify-center h-12 w-12 rounded-full bg-card border border-border text-sm font-medium">Present</div>
            </div>
          </div>
        </div>

        <div className="col-span-2 rounded-lg border border-border p-2">
          <h4 className="text-sm font-medium">Delegates & Authority</h4>
          <div className="mt-2 h-48 w-full rounded-md bg-muted p-2 overflow-hidden">
            <CircleOfAction />
          </div>
        </div>

        </div>
      </div>

      {/* Footer (sticky) */}
      <div className="flex items-center justify-between gap-3 p-3 border-t border-border bg-card/80">
        <div>
          <Button variant="ghost" size="sm" onClick={() => window.location.href = '/app/profile'}>Open full profile</Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { /* freeze action */ }}>Freeze account</Button>
          <Button size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
});

export default ProfilePanel;
